import {Component, OnDestroy, OnInit} from '@angular/core';
import {DictionaryService} from '@twpub/core/services';
import {TermwebStatusService} from '@twpub/core/services/termweb-status.service';
import {TenantService} from '@twpub/core/services/tenant.service';
import {DictionaryStatistics} from '@twpub/core/models/dictionary-statistics';
import {ConfirmDialogComponent} from '../confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {timeStr} from '@twpub/core/utils';
import {NotifierService} from '../../services/notifier.service';
import {interval, Subscription} from 'rxjs';
import {Router} from '@angular/router';
import {MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {DatePipe, NgClass} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import {SyncStatusIndicatorComponent} from '../sync-status-indicator/sync-status-indicator.component';

@Component({
  selector: 'pub-dictionaries-panel',
  templateUrl: './dictionaries-panel.component.html',
  styleUrls: ['./dictionaries-panel.component.scss'],
  standalone: true,
  imports: [MatIconButton, MatTooltip, MatIcon, MatProgressSpinner, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, SyncStatusIndicatorComponent, NgClass, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, DatePipe]
})
export class DictionariesPanelComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['order', 'id', 'name', 'twConceptCount', 'pubConceptCount', 'syncStatus', 'syncDate', 'syncTime', 'actions'];
  dictStats: DictionaryStatistics[] = [];
  isRefreshing = false;
  termWebAccessible = true;
  termWebStatusMessage = '';
  private pollSubscription?: Subscription;

  protected readonly timeStr = timeStr;

  constructor(
    private dictService: DictionaryService,
    private termwebStatusService: TermwebStatusService,
    private tenantService: TenantService,
    private dialog: MatDialog,
    private notifier: NotifierService,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    this.fetchStats();
    this.refreshData();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private startPolling(): void {
    if (this.pollSubscription) {
      return;
    }
    this.pollSubscription = interval(2000).subscribe(() => {
      this.fetchStats();
    });
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = undefined;
    }
  }

  private fetchStats() {
    this.dictService.getDictionaryStatistics().subscribe({
      next: (stats) => {
        const wasSyncing = this.pollSubscription != null;
        this.dictStats = stats;
        this.isRefreshing = false;

        const hasActiveTask = stats.some(s => s.syncStats?.status === 'IN_PROGRESS' || s.syncStats?.status === 'DELETING');
        if (hasActiveTask) {
          this.startPolling();
        } else {
          this.stopPolling();
          if (wasSyncing) {
            this.dictService.clearCache();
          }
        }
      }
    })
  }

  highlightedIndex: number | null = null;

  moveUp(index: number) {
    if (index <= 0) return;
    const arr = [...this.dictStats];
    [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
    this.dictStats = arr;
    this.highlightRow(index - 1);
    this.saveDictionaryOrder();
  }

  moveDown(index: number) {
    if (index >= this.dictStats.length - 1) return;
    const arr = [...this.dictStats];
    [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
    this.dictStats = arr;
    this.highlightRow(index + 1);
    this.saveDictionaryOrder();
  }

  private highlightRow(index: number) {
    this.highlightedIndex = index;
    setTimeout(() => this.highlightedIndex = null, 1200);
  }

  private saveDictionaryOrder() {
    const order = this.dictStats.map(s => s.id);
    this.tenantService.updateDictionaryOrder(order).subscribe({
      error: () => this.notifier.showPopupMessage('Failed to update dictionary order')
    });
  }

  refreshData() {
    this.isRefreshing = true;
    this.checkTermWebStatus();
    this.dictService.refreshDictionaries().subscribe({
      next: () => {
        this.fetchStats();
      },
      error: () => {
        this.isRefreshing = false;
        this.fetchStats();
      }
    })
  }

  private checkTermWebStatus() {
    this.termwebStatusService.getTermWebStatus().subscribe({
      next: (status) => {
        this.termWebAccessible = status.ok;
        this.termWebStatusMessage = status.ok ? '' : (status.message || 'TermWeb is not accessible');
      },
      error: () => {
        this.termWebAccessible = false;
        this.termWebStatusMessage = 'Could not check TermWeb status';
      }
    });
  }

  getConceptCount(stat: DictionaryStatistics) {
    if (this.isComplete(stat)) {
      return stat.publishedConceptCount;
    } else {
      return '-';
    }
  }

  fetchUpdates(stat: DictionaryStatistics) {
    if (this.isNotSynced(stat)) {
      this.reindexDictionaryPrompt(stat);
    } else {
      this.checkForUpdates(stat);
    }
  }

  reindexDictionaryPrompt(stat: DictionaryStatistics) {
    const syncStats = stat?.syncStats;
    let message: string[];
    if (syncStats?.status === 'IDLE') {
      message = ['Start indexing "' + stat?.name + '"?'];
    } else {
      message = ['Are you sure you want to reindex "' + stat?.name + '"?',
        syncStats?.initialSyncTimeMs ? 'Estimated time: ' + timeStr(syncStats.initialSyncTimeMs) : ''];
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {data: {message}});

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.dictService.reindexDictionary(stat.id).subscribe({
          next: () => {
            this.notifier.showPopupMessage(`Reindexing started for "${stat?.name}"`);
            this.setSyncInProgress(stat);
          },
          error: () => {
            this.fetchStats();
          }
        })
      }
    });
  }

  private checkForUpdates(stat: DictionaryStatistics) {
    this.dictService.refreshDictionary(stat.id).subscribe({
      next: () => {
        this.notifier.showPopupMessage(`Sync started for "${stat?.name}"`);
        this.setSyncInProgress(stat);
      },
      error: () => {
        this.fetchStats();
      }
    })
  }

  private setSyncInProgress(stat: DictionaryStatistics) {
    stat.syncStats = {...stat.syncStats, status: 'IN_PROGRESS'};
    this.dictStats = [...this.dictStats];
    this.startPolling();
  }

  isComplete(stat: DictionaryStatistics) {
    return stat.syncStats?.status === 'COMPLETE' || stat.syncStats?.status === 'WARNING';
  }

  isInProgess(stat: DictionaryStatistics) {
    return stat.syncStats.status === 'IN_PROGRESS' || stat.syncStats.status === 'DELETING';
  }

  isNotSynced(stat: DictionaryStatistics) {
    return stat.syncStats.status === 'IDLE';
  }

  isFailed(stat: DictionaryStatistics) {
    return stat.syncStats.status === 'FAILED';
  }

  openDictionaryDetails(dictStat: DictionaryStatistics): void {
    this.router.navigate(['/admin/dictionaries', dictStat.id]);
  }

  getNameTooltip(dictStat: DictionaryStatistics): string {
    if (dictStat.syncStats.status === 'DELETING') {
      return 'Deleting index...';
    } else if (this.isInProgess(dictStat)) {
      return 'Synchronizing...';
    } else if (this.isFailed(dictStat)) {
      return 'Synchronization failed: ' + (dictStat.syncStats.statusMessage || 'Unknown error');
    } else if (!this.isComplete(dictStat)) {
      return 'Dictionary is not synchronized';
    } else if (!dictStat.visible) {
      return 'No visible sections and/or languages';
    } else {
      return '';
    }
  }
}
